import { get } from 'lodash';
import JSZip from 'jszip';
import { readString } from 'react-papaparse';
import { newSiteName } from 'src/utils/mapUtils';
import type {
  ImportedKmlSite,
  SiteImportFileData,
  SiteImportFileType,
} from './siteImportModalTypes';

const jschardet = require('jschardet');

type ParseSiteImportFileOptions = {
  count: number;
  siteNameTemplate: string;
};

type ParsedSiteImportFile = {
  fileData: SiteImportFileData;
  fileName: string;
  fileType: SiteImportFileType;
};

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

function bytesToBinaryString(bytes: Uint8Array) {
  const chunkSize = 0x8000;
  let result = '';
  for (let index = 0; index < bytes.length; index += chunkSize) {
    result += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }
  return result;
}

function decodeText(bytes: Uint8Array, encoding?: string | null) {
  const normalizedEncoding = encoding?.toLowerCase() || 'utf-8';

  try {
    return new TextDecoder(normalizedEncoding).decode(bytes);
  } catch (error) {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

function extractSiteObjs(
  kmlData: string,
  siteNameTemplate: string,
  count: number
): ImportedKmlSite[] {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(kmlData, 'text/xml');
  const siteXMLObjs = xmlDoc.getElementsByTagName('Placemark');
  const siteObjs: ImportedKmlSite[] = [];

  for (let index = 0; index < siteXMLObjs.length; index += 1) {
    const nameObj = siteXMLObjs[index].getElementsByTagName('name');
    const descriptionObj = siteXMLObjs[index].getElementsByTagName('description');
    let siteName: string = get(nameObj, '0.childNodes.0.nodeValue');

    if (!siteName) {
      siteName = newSiteName({
        siteNameTemplate,
        count,
        increment: index + 1,
      });
    }

    const description: string | null = get(
      descriptionObj,
      '0.childNodes.0.nodeValue',
      null
    );
    const pointObj = siteXMLObjs[index].getElementsByTagName('Point');
    if (!pointObj.length) {
      continue;
    }

    const coordsNode = pointObj[0].getElementsByTagName('coordinates')[0];
    const coordsValue = get(coordsNode, 'childNodes.0.nodeValue', '');
    const coordsArr = coordsValue.split(',');
    const [lng, lat] = [coordsArr[0], coordsArr[1]];

    if (siteName != null && lng != null && lat != null) {
      siteObjs.push({ name: siteName, lat, lng, description });
    }
  }

  return siteObjs;
}

export function getSiteImportFileType(fileName: string): SiteImportFileType {
  const normalizedName = fileName.trim().toLowerCase();
  if (normalizedName.endsWith('.kmz')) {
    return 'kmz';
  }
  if (normalizedName.endsWith('.kml')) {
    return 'kml';
  }
  if (normalizedName.endsWith('.csv')) {
    return 'csv';
  }
  return null;
}

export async function parseSiteImportFile(
  file: File,
  options: ParseSiteImportFileOptions
): Promise<ParsedSiteImportFile> {
  const { count, siteNameTemplate } = options;
  const fileName = file.name;
  const fileType = getSiteImportFileType(fileName);

  if (fileType === null) {
    throw new Error('Unsupported file type.');
  }

  if (fileType === 'kmz') {
    const archive = await JSZip.loadAsync(file);
    const kmlFile = Object.values(archive.files).find(
      (archiveFile) =>
        !archiveFile.dir && getSiteImportFileType(archiveFile.name) === 'kml'
    );

    if (!kmlFile) {
      throw new Error('The data file provided is either empty or corrupt.');
    }

    const kmlData = await kmlFile.async('text');
    return {
      fileData: extractSiteObjs(kmlData, siteNameTemplate, count),
      fileName,
      fileType,
    };
  }

  const buffer = await readFileAsArrayBuffer(file);
  const bytes = new Uint8Array(buffer);

  if (fileType === 'kml') {
    return {
      fileData: extractSiteObjs(decodeText(bytes), siteNameTemplate, count),
      fileName,
      fileType,
    };
  }

  const binaryString = bytesToBinaryString(bytes);
  const detectedEncoding = jschardet.detect(binaryString).encoding;
  const csvText = decodeText(bytes, detectedEncoding);
  const parsedData: any = readString(csvText, {
    skipEmptyLines: 'greedy',
  } as any);

  return {
    fileData: parsedData.data,
    fileName,
    fileType,
  };
}
